import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { purgePersistedQueryCache } from '@/lib/queryPersist';
import './index.css';

// Force dark theme — light theme removed in Phase 21.1.
document.documentElement.classList.add('dark');
document.documentElement.classList.remove('light');

// Register service worker for web push + offline cache.
// Fire-and-forget; failures are logged only.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .then((reg) => {
        console.log('[SW] registered, scope:', reg.scope);
      })
      .catch((err) => {
        console.warn('[SW] registration failed:', err);
      });
  });
}

// Ask the browser to keep our cached media around even when disk is tight.
// Without this, the SW's media cache is "best effort" and may be evicted
// under storage pressure. With granted persistence, Chrome treats us as
// important and never silently clears the data.
if ('storage' in navigator && 'persist' in navigator.storage) {
  navigator.storage.persist().then((granted) => {
    console.log('[storage] persistent:', granted);
  }).catch(() => {});
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Keep inactive data warm for a short revisit, but do not retain entire
      // message histories for a day in a long-running browser process.
      gcTime: 30 * 60 * 1000,
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status: number } })?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
    },
  },
});

// Для messages-семейства queries: always fresh. Каждый mount/focus/reconnect
// force-refetch'ит историю с сервера; chatStore остаётся быстрым UI-cache.
// Семьи queryKeys (по убыванию специфичности):
//   ['messages']                              — root
//   ['messages', 'channel', channelId, ...]   — channel messages
//   ['messages', 'dm', peerId, ...]           — dm history
// setQueryDefaults применяется ко всем queries чей queryKey НАЧИНАЕТСЯ с ['messages'].
queryClient.setQueryDefaults(['messages'], {
  staleTime: 0,
  refetchOnMount: 'always',
  refetchOnWindowFocus: 'always',
  refetchOnReconnect: 'always',
});

// A broad React Query snapshot cannot be safely account-bound here because
// authStore intentionally persists no identity. Never hydrate it: purge old
// snapshots in the background and render from a fresh QueryClient.
import { useAuthStore } from '@/store/authStore';
void purgePersistedQueryCache();

// QueryClient is process-global. Clear it on every account transition so a
// rapid logout/login in the same tab cannot expose the previous account's
// friends, profile, signed URLs or pending mutation state.
useAuthStore.subscribe((state, previousState) => {
  if ((state.user?.id ?? null) !== (previousState.user?.id ?? null)) {
    queryClient.clear();
  }
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.StrictMode>,
);
